import { useEffect, useRef, useState } from 'react';
import { usePage } from '@inertiajs/react';
import { getCableConsumer } from '../lib/cableConsumer';

export function useUserNotifications(currentUserId) {
  const pendingInvite = usePage().props.pending_live_session_invite;
  const [invite, setInvite] = useState(
    pendingInvite ? { ...pendingInvite, received_at: Date.now() } : null
  );
  const subRef = useRef(null);

  // Sync invite state with the server-rendered shared prop on each
  // navigation:
  //  - If the server says no pending invite (e.g. the user just landed on
  //    the lobby for that session), clear any stale local state so the
  //    InviteIsland disappears.
  //  - Otherwise re-seed unless we already have an invite for the same
  //    session.
  useEffect(() => {
    if (!pendingInvite) {
      setInvite(null);
      return;
    }
    if (!invite || invite.live_session_id !== pendingInvite.live_session_id) {
      setInvite({ ...pendingInvite, received_at: Date.now() });
    }
  }, [pendingInvite?.live_session_id]);

  useEffect(() => {
    if (!currentUserId) return;

    const cable = getCableConsumer();
    subRef.current = cable.subscriptions.create(
      { channel: 'UserNotificationsChannel' },
      {
        rejected() {
          console.warn('[UserNotificationsChannel] subscription rejected');
        },
        received(data) {
          if (data?.type === 'live_session_invite') {
            setInvite({
              live_session_id: data.live_session_id,
              join_code: data.join_code,
              join_url: data.join_url,
              teacher_name: data.teacher_name,
              view: data.view,
              view_params: data.view_params,
              received_at: Date.now(),
            });
          }
        },
      }
    );

    return () => {
      if (subRef.current) {
        subRef.current.unsubscribe();
        subRef.current = null;
      }
    };
  }, [currentUserId]);

  const dismissInvite = () => setInvite(null);

  return { invite, dismissInvite };
}
